/**
 * SRP island loader: runs when we skip root hydration for static SRP.
 * Parses #srp-initial and mounts SrpApp (interactive) into #srp-app, then swaps with #srp-ssr so the page becomes interactive.
 * SrpApp is loaded dynamically to keep the initial bundle smaller and improve FCP/TBT.
 * Reuses the same root on client-side navigation to avoid "createRoot() on a container that has already been passed to createRoot()" error.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { parseSrpInitialScript, type SrpInitialPayload } from '../../features/srp/parseSrpInitialScript';
import type { SrpViewModel } from '../../features/srp/SrpViewModel';
import type { SessionUser } from '../../types/auth';
import type { PageContextClient } from 'vike/types';

/** Defer mount until interaction or this fallback (ms). Reduces TBT by not running React tree on load. */
const DEFER_MOUNT_FALLBACK_MS = 2500;

/** Reuse root on client-side navigation so we never call createRoot twice on the same container. */
let srpRoot: Root | null = null;
let srpRootContainer: HTMLElement | null = null;

/** Delay (ms) after mount before revealing app so React has committed and painted. */
const SWAP_DELAY_MS = 50;

/**
 * Replace static content with the app: reveal the app first (overlaid on static) so there is no blank
 * frame, then hide the static node (don't remove) and put the app in flow. We hide instead of remove
 * so that when the SRP shell was rendered by React (client-side nav from another page), React's tree
 * stays valid. The static shell gets class srp-ssr--replaced so CSS can keep it hidden.
 */
function runSwap(ssr: HTMLElement, app: HTMLElement): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const wrapper = app.parentElement;

        // 1. Hide static first so we never paint a frame with both visible (avoids duplicate hits)
        ssr.style.display = 'none';
        ssr.style.visibility = 'hidden';
        ssr.setAttribute('aria-hidden', 'true');
        ssr.classList.add('srp-ssr--replaced');

        // 2. Lock wrapper height so layout doesn't collapse
        if (wrapper) {
          wrapper.style.minHeight = `${wrapper.offsetHeight}px`;
        }

        // 3. Reveal app and put in flow
        app.style.visibility = 'visible';
        app.style.pointerEvents = 'auto';
        app.removeAttribute('aria-hidden');
        app.style.position = 'static';

        // 4. Keep wrapper at least 100vh to prevent layout shift when app height differs from static
        if (wrapper) {
          requestAnimationFrame(() => {
            wrapper.style.minHeight = '100vh';
          });
        }
      }, SWAP_DELAY_MS);
    });
  });
}

async function resolveViewModel(payload: SrpInitialPayload): Promise<SrpViewModel | null> {
  if ('_fetch' in payload && payload._fetch === true) {
    const url = `/api/srp-state?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`;
    const res = await fetch(url);
    if (!res.ok) {
      console.error('[srp.client] Failed to fetch SRP state:', res.status, res.statusText);
      return null;
    }
    return res.json() as Promise<SrpViewModel>;
  }
  return payload as SrpViewModel;
}

async function mountIsland(
  container: HTMLElement,
  scriptEl: HTMLScriptElement,
  initialUser: SessionUser | null
): Promise<void> {
  container.style.visibility = 'hidden';

  const scriptText = scriptEl.textContent ?? '';
  const payload = parseSrpInitialScript(scriptText);
  if (!payload) {
    container.style.visibility = 'visible';
    return;
  }

  const viewModel = await resolveViewModel(payload);
  if (!viewModel) {
    container.style.visibility = 'visible';
    return;
  }

  const { SrpApp } = await import('../../features/srp/SrpApp');
  const tree = React.createElement(SrpApp, {
    initialViewModel: viewModel,
    mode: 'interactive',
    initialUser,
  });

  const scheduleSwap = (): void => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const ssr = document.getElementById('srp-ssr');
        if (ssr) {
          runSwap(ssr, container);
        } else {
          revealAppAndHideSsr(container);
        }
      });
    });
  };

  if (srpRoot && srpRootContainer === container) {
    srpRoot.render(tree);
    scheduleSwap();
    return;
  }

  if (srpRoot && srpRootContainer) {
    srpRoot.unmount();
    srpRoot = null;
    srpRootContainer = null;
  }

  srpRoot = createRoot(container);
  srpRootContainer = container;
  srpRoot.render(tree);
  scheduleSwap();
}

/** When #srp-ssr is not found, still reveal the app and try to hide static shell to prevent duplicate content. */
function revealAppAndHideSsr(container: HTMLElement): void {
  const ssr = document.getElementById('srp-ssr');
  if (ssr) {
    ssr.style.display = 'none';
    ssr.style.visibility = 'hidden';
    ssr.setAttribute('aria-hidden', 'true');
    ssr.classList.add('srp-ssr--replaced');
  }
  container.style.visibility = 'visible';
  container.style.pointerEvents = 'auto';
  container.removeAttribute('aria-hidden');
  container.style.position = 'static';
  const wrapper = container.parentElement;
  if (wrapper) {
    wrapper.style.minHeight = '';
  }
}

export interface RunSrpIslandLoaderOptions {
  /** When true (e.g. we skipped root hydration), mount immediately instead of deferring. */
  mountImmediately?: boolean;
}

export function runSrpIslandLoader(
  pageContext: PageContextClient,
  options: RunSrpIslandLoaderOptions = {}
): void {
  const appEl = document.getElementById('srp-app');
  const scriptEl = document.getElementById('srp-initial');
  if (!appEl || !scriptEl) return;

  let mounted = false;
  let fallbackHandle: ReturnType<typeof setTimeout> | null = null;

  const initialUser = (pageContext?.user ?? null) as SessionUser | null;

  const doMount = (): void => {
    if (mounted) return;
    mounted = true;
    if (fallbackHandle !== null) {
      clearTimeout(fallbackHandle);
      fallbackHandle = null;
    }
    mountIsland(appEl, scriptEl, initialUser);
  };

  const onInteraction = (): void => {
    doMount();
    document.removeEventListener('pointerdown', onInteraction);
    document.removeEventListener('keydown', onInteraction);
    document.removeEventListener('focus', onInteraction, true);
  };

  document.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
  document.addEventListener('keydown', onInteraction, { once: true, passive: true });
  document.addEventListener('focus', onInteraction, { once: true, capture: true });

  if (options.mountImmediately) {
    doMount();
  } else {
    fallbackHandle = setTimeout(doMount, DEFER_MOUNT_FALLBACK_MS);
  }
}
